import { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const STORAGE_KEY = 'agentInstallerSelection'

const STEPS = [
  {
    title: 'Download the installer',
    description: 'Save the Helpdesk 411 agent installer to your local machine.',
  },
  {
    title: 'Run the installer as Administrator',
    description: 'Right-click the installer and choose “Run as administrator” to ensure the agent has the required permissions.',
  },
  {
    title: 'Optional: Silent install via command prompt',
    description: 'Use the following command to run a silent installation if you are deploying across multiple devices.',
    code: 'msiexec /i "helpdesk411-agent.msi" /quiet /norestart',
  },
  {
    title: 'Verify installation',
    description: 'Open the Helpdesk 411 application and ensure your device appears online. Contact your manager if you encounter issues.',
  },
]

interface StoredSelection {
  os: string
  arch: string
  inviteId?: string | null
}

export const AgentInstallPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const selection = useMemo(() => {
    const paramsSelection: StoredSelection | null = searchParams.get('os')
      ? {
          os: searchParams.get('os') || 'windows',
          arch: searchParams.get('arch') || 'x64',
          inviteId: searchParams.get('inviteId'),
        }
      : null
    if (paramsSelection) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(paramsSelection))
      return paramsSelection
    }

    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored) as StoredSelection
      } catch {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }
    return null
  }, [searchParams])

  useEffect(() => {
    if (!selection) {
      navigate('/agent/download', { replace: true })
    }
  }, [selection, navigate])

  if (!selection) {
    return null
  }

  const installSuccessPath = `/agent/success?os=${selection.os}&arch=${selection.arch}${
    selection.inviteId ? `&inviteId=${selection.inviteId}` : ''
  }`

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center px-4 py-12">
      <div className="mb-10 flex flex-col items-center gap-3">
        <img src="/black_logo.svg" alt="Helpdesk 411" className="h-12 w-auto" />
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <span className="inline-flex h-2 w-2 rounded-full bg-gray-300" />
          <span className="inline-flex h-2 w-2 rounded-full bg-gray-300" />
          <span className="inline-flex h-2 w-2 rounded-full bg-red-500" />
        </div>
      </div>

      <div className="w-full max-w-3xl rounded-3xl bg-white p-10 shadow-sm border border-gray-100">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Installation Instructions</h1>
          <button
            type="button"
            onClick={() => navigate(`/agent/download${selection.inviteId ? `?inviteId=${selection.inviteId}` : ''}`)}
            className="text-sm font-medium text-gray-500 hover:text-gray-900"
          >
            Need the installer again?
          </button>
        </div>

        <p className="mb-8 text-sm text-gray-600">
          Follow the steps below to install the Helpdesk 411 agent on your device (
          {selection.os.toUpperCase()} · {selection.arch.toUpperCase()}).
        </p>

        <div className="space-y-4">
          {STEPS.map((step, index) => (
            <div key={step.title} className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                  {index + 1}
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{step.title}</h2>
                  <p className="mt-2 text-sm text-gray-600">{step.description}</p>
                  {step.code && (
                    <pre className="mt-3 rounded-2xl bg-[#0f172a] px-4 py-3 text-xs font-mono text-[#f8fafc] overflow-x-auto">
{step.code}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(installSuccessPath)}
            className="inline-flex items-center rounded-full bg-green-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-700"
          >
            I’ve installed the agent
          </button>
          <button
            type="button"
            onClick={() => navigate(`/agent/download${selection.inviteId ? `?inviteId=${selection.inviteId}` : ''}`)}
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Back to download
          </button>
        </div>
      </div>
    </div>
  )
}
